import React, { useState, useEffect } from 'react'
import { Modal, Button } from '../ui'
import { useThemeTokens } from '../../themes'
import { validateTimesheetForSubmission } from '../../lib/submission-validation'
import type { DailyTimesheet, SubmissionValidationResult } from '../../contracts/timesheet'

interface SubmissionModalProps {
  isOpen: boolean
  onClose: () => void
  timesheet: DailyTimesheet | null
  onSubmit: (timesheet: DailyTimesheet) => Promise<void>
  isSubmitting: boolean
}

export const SubmissionModal: React.FC<SubmissionModalProps> = ({
  isOpen,
  onClose,
  timesheet,
  onSubmit,
  isSubmitting
}) => {
  const theme = useThemeTokens()
  const [validationResults, setValidationResults] = useState<SubmissionValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)

  // Validate timesheet when modal opens or timesheet changes
  useEffect(() => {
    if (isOpen && timesheet) {
      setIsValidating(true)
      validateTimesheetForSubmission(timesheet)
        .then(setValidationResults)
        .finally(() => setIsValidating(false))
    } else {
      setValidationResults(null)
    }
  }, [isOpen, timesheet])

  const handleSubmit = async () => {
    if (!timesheet || !validationResults?.valid) return
    
    try {
      await onSubmit(timesheet)
      onClose()
    } catch (error) {
      console.error('Submission failed:', error)
      // Error handling is managed by the parent component
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  const getJobSiteName = (): string => {
    if (timesheet?.jobSite?.name) {
      return timesheet.jobSite.name
    }
    return timesheet?.jobSiteId ? `Job Site #${timesheet.jobSiteId}` : 'Unknown Job Site'
  }

  if (!timesheet) return null

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Submit Timesheet"
      size="lg"
      data-testid="submission-modal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
        {/* Timesheet Summary */}
        <div style={{
          backgroundColor: theme.colors.secondary + '10',
          padding: theme.spacing.lg,
          borderRadius: theme.borderRadius.lg,
          border: `1px solid ${theme.colors.border}`
        }}>
          <h3 style={{
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            margin: `0 0 ${theme.spacing.md} 0`,
            fontFamily: theme.typography.fontFamily
          }}>
            📅 {formatDate(timesheet.date)}
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: theme.spacing.md,
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.text
          }}>
            <div>👥 <strong>{timesheet.totalWorkers}</strong> workers</div>
            <div>⏰ <strong>{timesheet.totalHours.toFixed(1)}</strong> total hours</div>
            <div>🏗️ <strong>{getJobSiteName()}</strong></div>
            <div>📋 Pay Period: <strong>#{timesheet.payPeriodId}</strong></div>
          </div>

          {timesheet.scanFileUrl && (
            <div style={{ marginTop: theme.spacing.md, fontSize: theme.typography.fontSize.sm }}>
              📄 <strong>Attached Files:</strong> Scan uploaded
            </div>
          )}
        </div>

        {/* Validation Results */}
        {isValidating && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing.lg,
            color: theme.colors.textSecondary
          }}>
            Validating timesheet...
          </div>
        )}

        {validationResults && !isValidating && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            {/* Validation Errors */}
            {validationResults.errors.length > 0 && (
              <div>
                <h4 style={{
                  fontSize: theme.typography.fontSize.md,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.error,
                  margin: `0 0 ${theme.spacing.sm} 0`,
                  fontFamily: theme.typography.fontFamily
                }}>
                  ❌ Errors (Must be fixed before submission)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                  {validationResults.errors.map((error, index) => (
                    <div key={index} style={{
                      padding: theme.spacing.sm,
                      backgroundColor: theme.colors.error + '10',
                      border: `1px solid ${theme.colors.error}30`,
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.error
                    }}>
                      ⚠️ {error.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Validation Warnings */}
            {validationResults.warnings.length > 0 && (
              <div>
                <h4 style={{
                  fontSize: theme.typography.fontSize.md,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: '#f59e0b', // Orange color for warnings
                  margin: `0 0 ${theme.spacing.sm} 0`,
                  fontFamily: theme.typography.fontFamily
                }}>
                  ⚠️ Warnings (Please review)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                  {validationResults.warnings.map((warning, index) => (
                    <div key={index} style={{
                      padding: theme.spacing.sm,
                      backgroundColor: '#fef3c7', // Light yellow background
                      border: '1px solid #fde68a', // Yellow border
                      borderRadius: theme.borderRadius.sm,
                      fontSize: theme.typography.fontSize.sm,
                      color: '#f59e0b' // Orange text
                    }}>
                      ⚠️ {warning.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Message */}
            {validationResults.valid && validationResults.errors.length === 0 && (
              <div style={{
                padding: theme.spacing.md,
                backgroundColor: theme.colors.statusConfirmed + '20',
                border: `1px solid ${theme.colors.statusConfirmed}30`,
                borderRadius: theme.borderRadius.sm,
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.statusConfirmed
              }}>
                ✅ Timesheet is ready for submission
              </div>
            )}
          </div>
        )}

        {/* Important Notice */}
        <div style={{
          backgroundColor: '#fef3c7', // Light yellow background
          border: '1px solid #fde68a', // Yellow border
          padding: theme.spacing.md,
          borderRadius: theme.borderRadius.sm
        }}>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: '#f59e0b', // Orange text
            margin: 0,
            fontFamily: theme.typography.fontFamily
          }}>
            ℹ️ <strong>Important:</strong> Once submitted, this timesheet cannot be edited. 
            A new revision can be created if changes are needed.
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: theme.spacing.md,
          paddingTop: theme.spacing.md,
          borderTop: `1px solid ${theme.colors.border}`
        }}>
          <Button 
            variant="secondary" 
            onClick={onClose} 
            disabled={isSubmitting || isValidating}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={
              !validationResults?.valid || 
              isSubmitting || 
              isValidating ||
              validationResults?.errors?.length > 0
            }
            data-testid="confirm-submit-btn"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Timesheet'}
          </Button>
        </div>
      </div>
    </Modal>
  )
} 